export type PosePoint = {
  x: number;
  y: number;
  z: number;
  visibility?: number;
};

export const RIGHT_CONNECTIONS = [
  [12, 14], [14, 16], [16, 18], [16, 20], [16, 22],
  [24, 26], [26, 28], [28, 30], [28, 32], [30, 32],
] as const;

export const LEFT_CONNECTIONS = [
  [11, 13], [13, 15], [15, 17], [15, 19], [15, 21],
  [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
] as const;

export const CORE_CONNECTIONS = [
  [11, 12], [23, 24], [11, 23], [12, 24],
] as const;

const RIGHT_INDICES = new Set<number>(RIGHT_CONNECTIONS.flat());
const LEFT_INDICES = new Set<number>(LEFT_CONNECTIONS.flat());
const BODY_INDICES = new Set([...RIGHT_INDICES, ...LEFT_INDICES]);
const VISIBILITY_THRESHOLD = 0.5;

type DrawOptions = { pixelRatio?: number };

export function fitPoseStage(availableWidth: number, aspectRatio: number, maxHeight = 360): { width: number; height: number } {
  if (!Number.isFinite(availableWidth) || availableWidth <= 0 || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return { width: 1, height: 1 };
  }
  const width = Math.min(availableWidth, maxHeight * aspectRatio);
  return { width, height: width / aspectRatio };
}

function visible(point: PosePoint | undefined): point is PosePoint {
  return Boolean(point && (point.visibility ?? 1) >= VISIBILITY_THRESHOLD);
}

function colorFor(index: number): string {
  if (RIGHT_INDICES.has(index)) return "#00E5FF";
  if (LEFT_INDICES.has(index)) return "#FF9100";
  return "rgba(255, 255, 255, 0.82)";
}

export function drawPremiumSkeleton(
  context: CanvasRenderingContext2D,
  points: PosePoint[],
  options: DrawOptions = {},
): void {
  const ratio = options.pixelRatio ?? 1;
  const { width, height } = context.canvas;
  context.clearRect(0, 0, width, height);
  context.lineCap = "round";
  context.lineJoin = "round";
  context.lineWidth = 1.5 * ratio;

  const drawSegments = (connections: readonly (readonly [number, number])[], color: string) => {
    context.strokeStyle = color;
    for (const [start, end] of connections) {
      const first = points[start];
      const second = points[end];
      if (!visible(first) || !visible(second)) continue;
      context.beginPath();
      context.moveTo(first.x * width, first.y * height);
      context.lineTo(second.x * width, second.y * height);
      context.stroke();
    }
  };

  drawSegments(RIGHT_CONNECTIONS, "#00E5FF");
  drawSegments(LEFT_CONNECTIONS, "#FF9100");
  drawSegments(CORE_CONNECTIONS, "rgba(255, 255, 255, 0.82)");

  for (const index of BODY_INDICES) {
    const point = points[index];
    if (!visible(point)) continue;
    context.fillStyle = colorFor(index);
    context.strokeStyle = "rgba(4, 16, 30, 0.72)";
    context.lineWidth = ratio;
    context.beginPath();
    context.arc(point.x * width, point.y * height, 3 * ratio, 0, Math.PI * 2);
    context.fill();
    context.stroke();
  }
}

export function drawSpatialPose(
  context: CanvasRenderingContext2D,
  points: PosePoint[],
  options: DrawOptions = {},
): void {
  const ratio = options.pixelRatio ?? 1;
  const { width, height } = context.canvas;
  context.clearRect(0, 0, width, height);
  context.fillStyle = "rgba(5, 17, 31, 0.72)";
  context.fillRect(0, 0, width, height);

  const margin = 12 * ratio;
  context.strokeStyle = "rgba(255, 255, 255, 0.13)";
  context.lineWidth = ratio;
  context.strokeRect(margin, margin, width - margin * 2, height - margin * 2);

  const hip = points[23] && points[24]
    ? { x: (points[23].x + points[24].x) / 2, y: (points[23].y + points[24].y) / 2, z: (points[23].z + points[24].z) / 2 }
    : { x: 0, y: 0, z: 0 };
  const scale = Math.min(width, height) * 0.72;
  const project = (point: PosePoint) => ({
    x: width / 2 + ((point.x - hip.x) + (point.z - hip.z) * 0.38) * scale,
    y: height / 2 + ((point.y - hip.y) - (point.z - hip.z) * 0.12) * scale,
  });

  const drawSegments = (connections: readonly (readonly [number, number])[], color: string) => {
    context.strokeStyle = color;
    context.lineWidth = 1.2 * ratio;
    for (const [start, end] of connections) {
      const first = points[start];
      const second = points[end];
      if (!visible(first) || !visible(second)) continue;
      const a = project(first);
      const b = project(second);
      context.beginPath();
      context.moveTo(a.x, a.y);
      context.lineTo(b.x, b.y);
      context.stroke();
    }
  };

  drawSegments(RIGHT_CONNECTIONS, "#00E5FF");
  drawSegments(LEFT_CONNECTIONS, "#FF9100");
  drawSegments(CORE_CONNECTIONS, "rgba(255, 255, 255, 0.82)");
}

export function averagePoseVisibility(points: PosePoint[]): number {
  if (!points.length) return 0;
  return points.reduce((total, point) => total + (point.visibility ?? 0), 0) / points.length;
}
